import { Box, Button, Group, Stack, Text } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTootip, XAxis, YAxis } from 'recharts';
import { generateCSV, isSameDay, useBot, useChartDatas } from '@mygpt/app';
import CustomContainer from '@/components/CustomContainer';
import CustomContainerHeader from '@/components/CustomContainer/CustomContainerHeader';
import CustomTooltip from '@/components/Dashboard/Chart/components/CustomTooltip';
import ChartTable from '@/components/Dashboard/ChartTable';
import { SvgFileDownload } from '@/components/Dashboard/icon';
import TitleCard from '@/components/Dashboard/TitleCard';

const TotalMessages = () => {
  const { t } = useTranslation();
  const { botId } = useBot();
  const { datas, times, timesText } = useChartDatas();

  const totalMessages = useMemo(() => {
    let total = 0;
    datas.map((data) => {
      total += data.message_count;
    });
    return total;
  }, [datas]);

  const data = useMemo(() => {
    const chartData: { name: string; messages: number }[] = [];
    times.map((item) => {
      let messages = 0;
      datas.map((data) => {
        if (isSameDay(item, data.updated_at)) messages += data.message_count;
      });

      const obj = {
        name: item,
        messages,
      };
      chartData.push(obj);
    });
    return chartData;
  }, [datas, times]);

  const tableData = useMemo(() => {
    const value = times.map((time) => {
      let sum = 0;
      datas.map((data) => {
        if (isSameDay(time, data.updated_at)) sum += data.message_count;
      });
      return {
        key: time,
        value: sum,
      };
    });
    return [
      {
        type: t('dashboard.card.total_messages'),
        value,
      },
    ];
  }, [datas]);

  const DownloadButton = () => {
    const download = () => {
      const fileName = `GBase_${botId}_${t('dashboard.card.total_messages')}`;
      const header = [t('dashboard.series'), ...times, t('dashboard.total')];
      // 将每个数据对象转换为CSV格式的一行
      const rows = tableData.map((item) => {
        const rowsData = item.value;
        let total = 0;
        const everyCol = rowsData.map((row) => {
          total += row.value;
          return String(row.value);
        });
        return [item.type, ...everyCol, String(total)];
      });

      const data = {
        header,
        rows,
      };
      generateCSV(fileName, data);
    };

    return (
      <Button size="sm" variant="subtle" leftSection={<SvgFileDownload />} onClick={download}>
        <Text style={{ color: '#7C68FB' }}>{t('dashboard.export_csv')}</Text>
      </Button>
    );
  };

  return (
    <>
      <CustomContainerHeader
        title={t('dashboard.card.total_messages')}
        label={t('dashboard.card.total_messages_tip')}
      />
      <CustomContainer>
        <Stack gap={50}>
          <Group>
            <TitleCard title={t('dashboard.card.total_messages')} value={totalMessages} desc={timesText} />
          </Group>

          <Box w={'100%'} h={350} className="text-sm text-[#667085] font-light">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                width={500}
                height={300}
                data={data}
                barGap={48}
                barCategoryGap={48}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid stroke="#E4E7EC" strokeDasharray="0 0" vertical={false} />
                <XAxis axisLine={false} tickLine={false} tickMargin={10} padding={{ left: 45 }} dataKey="name" />
                <YAxis axisLine={false} tickLine={false} tickMargin={10} />
                <RechartsTootip content={<CustomTooltip text={{ messages: t('dashboard.table.messages') }} />} />
                <Bar dataKey="messages" fill="#7C68FB" />
              </BarChart>
            </ResponsiveContainer>
          </Box>

          <ChartTable
            title={t('dashboard.table.total_messages_title')}
            titleRightSection={<DownloadButton />}
            times={times}
            data={tableData}
          />
        </Stack>
      </CustomContainer>
    </>
  );
};

export default TotalMessages;
